import { Fragment } from 'react';

import {
  ArrowPathIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ChatBubbleLeftRightIcon,
  MinusIcon,
} from '@heroicons/react/24/outline';

import { useTranslations } from '@/i18n/client';
import { parseUtcDate } from '@/utils/date-time';
import { useOrderNotes } from './useOrderNotes';

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parseUtcDate(value));
}

export function OrderNotes() {
  const t = useTranslations('common.orderDetails');
  const tErrors = useTranslations('errors.api');

  const {
    client,
    orderId,

    notes,
    notesLoading,
    notesErrorKey,

    messageText,
    setMessageText,

    sendingNote,
    sendErrorKey,
    hasConflict,

    autoMarkAsRead,
    handleAutoMarkAsReadChange,

    unreadLabNoteCount,
    firstUnreadLabNoteId,

    isMessagesCollapsed,
    isMessagesLarge,
    toggleMessagesCollapsed,
    toggleMessagesLarge,

    messagesScrollRef,

    reloadOrderNotes,
    handleMarkAsRead,
    handleSendNote,
  } = useOrderNotes();

  const unreadMessagesDividerLabel =
    unreadLabNoteCount > 0
      ? t('messagesSection.unreadMessagesDivider', {
          count: unreadLabNoteCount,
        } as never)
      : '';

  return (
    <section className="rounded-[var(--zs-radius-m)] border border-[var(--md-border)] bg-[var(--md-white)] p-6 shadow-[var(--md-shadow-soft)]">
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <h2>{t('messagesSection.title')}</h2>

        <div className="flex flex-wrap items-center gap-3">
          {!isMessagesCollapsed && client && orderId ? (
            <>
              {unreadLabNoteCount > 0 ? (
                <button
                  type="button"
                  className="zs-button zs-button--secondary"
                  disabled={notesLoading}
                  onClick={() => void handleMarkAsRead()}
                >
                  {t('messagesSection.markAsReadAction')}
                </button>
              ) : null}

              <label className="flex items-center gap-2 text-sm text-[var(--zs-muted)]">
                <input
                  type="checkbox"
                  checked={autoMarkAsRead}
                  onChange={(event) => handleAutoMarkAsReadChange(event.target.checked)}
                />

                {t('messagesSection.autoMarkAsReadLabel')}
              </label>

              <button
                type="button"
                className="zs-button zs-button--secondary md-icon-button"
                disabled={notesLoading}
                onClick={() => void reloadOrderNotes()}
                aria-label={t('messagesSection.refreshAction')}
                title={t('messagesSection.refreshAction')}
              >
                <ArrowPathIcon
                  className={notesLoading ? 'md-icon-button__icon animate-spin' : 'md-icon-button__icon'}
                  aria-hidden="true"
                />
              </button>

              <button
                type="button"
                className="zs-button zs-button--secondary md-icon-button"
                onClick={toggleMessagesLarge}
                aria-label={
                  isMessagesLarge
                    ? t('messagesSection.normalSizeAction')
                    : t('messagesSection.largeSizeAction')
                }
                title={
                  isMessagesLarge
                    ? t('messagesSection.normalSizeAction')
                    : t('messagesSection.largeSizeAction')
                }
              >
                {isMessagesLarge ? (
                  <ArrowsPointingInIcon className="md-icon-button__icon" aria-hidden="true" />
                ) : (
                  <ArrowsPointingOutIcon className="md-icon-button__icon" aria-hidden="true" />
                )}
              </button>
            </>
          ) : null}

          <button
            type="button"
            className="zs-button zs-button--secondary md-icon-button"
            aria-expanded={!isMessagesCollapsed}
            aria-label={
              isMessagesCollapsed ? t('messagesSection.expandAction') : t('messagesSection.collapseAction')
            }
            title={
              isMessagesCollapsed ? t('messagesSection.expandAction') : t('messagesSection.collapseAction')
            }
            onClick={toggleMessagesCollapsed}
          >
            {isMessagesCollapsed ? (
              <ChatBubbleLeftRightIcon className="md-icon-button__icon" aria-hidden="true" />
            ) : (
              <MinusIcon className="md-icon-button__icon" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {!isMessagesCollapsed ? (
        <>
          <div
            ref={messagesScrollRef}
            className={`mt-3 overflow-y-auto rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-bg)] p-3 ${
              isMessagesLarge ? 'h-[44rem]' : 'h-[28rem]'
            }`}
          >
            {notesLoading ? (
              <p className="m-0 text-[var(--zs-muted)]">{t('messagesSection.loading')}</p>
            ) : null}

            {notesErrorKey ? (
              <p className="m-0 text-[var(--md-highlight)]">{tErrors(notesErrorKey)}</p>
            ) : null}

            {!notesLoading && !notesErrorKey && notes.length === 0 ? (
              <p className="m-0 text-[var(--zs-muted)]">{t('messagesSection.empty')}</p>
            ) : null}

            {!notesLoading && notes.length > 0 ? (
              <ul
                className="m-0 flex list-none flex-col gap-2.5 pl-0"
                aria-label={t('messagesSection.title')}
              >
                {notes.map((note) => {
                  const isUnreadLabNote = !note.isCustomerNote && !note.readUtc;

                  const isOwnReadNote = note.isCustomerNote && Boolean(note.readUtc);

                  const showUnreadDivider = note.id === firstUnreadLabNoteId;

                  return (
                    <Fragment key={note.id}>
                      {showUnreadDivider ? (
                        <li
                          className="my-2 flex items-center gap-3 text-xs font-bold text-[var(--md-highlight)]"
                          role="separator"
                          aria-label={unreadMessagesDividerLabel}
                        >
                          <span className="h-px flex-1 bg-[color-mix(in_oklab,var(--md-highlight)_35%,transparent)]" />

                          <span className="rounded-full bg-[color-mix(in_oklab,var(--md-highlight)_12%,var(--md-white))] px-3 py-1">
                            {unreadMessagesDividerLabel}
                          </span>

                          <span className="h-px flex-1 bg-[color-mix(in_oklab,var(--md-highlight)_35%,transparent)]" />
                        </li>
                      ) : null}

                      <li
                        className={`max-w-[85%] rounded-[var(--zs-radius-s)] border px-3.5 py-2.5 text-[0.9rem] ${
                          note.isCustomerNote
                            ? 'ml-auto border-[var(--md-border)] bg-[var(--md-primary-light)]'
                            : isUnreadLabNote
                              ? 'mr-auto border-[color-mix(in_oklab,var(--md-highlight)_55%,var(--md-primary))] bg-[color-mix(in_oklab,var(--md-highlight)_14%,var(--md-white))] shadow-[0_0_0_1px_color-mix(in_oklab,var(--md-highlight)_30%,transparent)]'
                              : 'mr-auto border-[var(--md-border)] bg-[var(--md-white)]'
                        }`}
                      >
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <strong className="text-[var(--md-primary)]">{note.createdByText}</strong>

                          <span className="inline-flex items-center gap-1.5 text-xs text-[var(--zs-muted)]">
                            {formatDateTime(note.createdUtc)}

                            {isOwnReadNote ? (
                              <span
                                className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-green-100 text-[0.7rem] font-bold text-green-700"
                                aria-label={t('messagesSection.readLabel')}
                                title={t('messagesSection.readLabel')}
                              >
                                ✓
                              </span>
                            ) : null}
                          </span>
                        </div>

                        <p className="m-0 mt-1 whitespace-pre-wrap">{note.content}</p>
                      </li>
                    </Fragment>
                  );
                })}
              </ul>
            ) : null}
          </div>

          {hasConflict ? (
            <p className="mt-3 text-[var(--md-highlight)]">{t('messagesSection.conflictNotice')}</p>
          ) : null}

          {sendErrorKey ? <p className="mt-3 text-[var(--md-highlight)]">{tErrors(sendErrorKey)}</p> : null}

          <form className="mt-4 flex flex-col gap-2.5" onSubmit={(event) => void handleSendNote(event)}>
            <label htmlFor="order-message">{t('messagesSection.messageLabel')}</label>

            <textarea
              id="order-message"
              rows={5}
              required
              disabled={sendingNote}
              className="min-h-28 rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-bg)] px-3 py-2.5 text-[var(--md-text)]"
              value={messageText}
              onChange={(event) => setMessageText(event.target.value)}
              placeholder={t('messagesSection.messagePlaceholder')}
            />

            <button type="submit" className="zs-button" disabled={sendingNote}>
              {t('messagesSection.sendAction')}
            </button>
          </form>
        </>
      ) : null}
    </section>
  );
}
